import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Plus, MoreHorizontal, Eye, Edit3, Power, CreditCard, ToggleRight, ExternalLink, Building2, Loader2, Trash2 } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import {
  BackendPlanTier,
  BackendTenantStatus,
  CreateTenantBody,
  PlatformTenant,
  UpdateTenantBody,
  platformTenantsApi,
  statusToLabel,
  tierToLabel,
} from "@/lib/platform-api";
import { ApiError } from "@/lib/api";
import { validateRequired } from "@/lib/form-validation";
import { TenantSubNav } from "@/components/platform/TenantSubNav";

type StatusLabel = ReturnType<typeof statusToLabel>;
type TierLabel = ReturnType<typeof tierToLabel>;

const statusStyle: Record<StatusLabel, string> = {
  Active: "border-success/30 bg-success/10 text-success",
  Suspended: "border-destructive/30 bg-destructive/10 text-destructive",
  Trial: "border-warning/30 bg-warning/10 text-warning",
  Pending: "border-muted-foreground/30 bg-muted text-muted-foreground",
};

const planStyle: Record<TierLabel, string> = {
  Basic: "border-muted-foreground/30 bg-muted text-foreground",
  Pro: "border-info/30 bg-info/10 text-info",
  Premium: "border-primary/30 bg-primary/10 text-primary",
  Enterprise: "border-accent/30 bg-accent/10 text-accent",
};

const PLAN_OPTIONS: { value: BackendPlanTier; label: TierLabel }[] = [
  { value: "BASIC", label: "Basic" },
  { value: "PRO", label: "Pro" },
  { value: "PREMIUM", label: "Premium" },
  { value: "ENTERPRISE", label: "Enterprise" },
];

const STATUS_OPTIONS: { value: BackendTenantStatus; label: StatusLabel }[] = [
  { value: "ACTIVE", label: "Active" },
  { value: "TRIAL", label: "Trial" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "PENDING", label: "Pending" },
];

type NewTenantForm = {
  name: string;
  code: string;
  industry: string;
  region: string;
  plan: BackendPlanTier;
  maxUsers: string;
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
};

const DEFAULT_FORM: NewTenantForm = {
  name: "",
  code: "",
  industry: "",
  region: "",
  plan: "PRO",
  maxUsers: "500",
  ownerName: "",
  ownerEmail: "",
  ownerPhone: "",
};

type EditTenantForm = {
  name: string;
  industry: string;
  region: string;
  status: BackendTenantStatus;
  primaryContact: string;
  contactEmail: string;
  renewalDate: string;
};

export default function PlatformTenants() {
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  // Seed from the header's global search (`?q=`), if present.
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("q") ?? "");
  const [planFilter, setPlanFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NewTenantForm>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [editing, setEditing] = useState<PlatformTenant | null>(null);
  const [editForm, setEditForm] = useState<EditTenantForm | null>(null);
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [deleting, setDeleting] = useState<PlatformTenant | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    platformTenantsApi
      .list({
        pageSize: 100,
        search: search || undefined,
        plan: planFilter === "all" ? undefined : (planFilter as BackendPlanTier),
        status: statusFilter === "all" ? undefined : (statusFilter as BackendTenantStatus),
      })
      .then((res) => { if (!cancelled) setTenants(res.data); })
      .catch((e) => {
        if (!cancelled) toast.error("Failed to load tenants", { description: errMsg(e) });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search, planFilter, statusFilter, reloadKey]);

  const planCounts = useMemo(() => {
    const counts = { BASIC: 0, PRO: 0, PREMIUM: 0, ENTERPRISE: 0 } as Record<BackendPlanTier, number>;
    for (const t of tenants) counts[t.plan.tier]++;
    return counts;
  }, [tenants]);

  const handleImpersonate = async (id: string, name: string) => {
    const res = await startImpersonation({ tenantId: id, tenantName: name });
    if (!res.ok) {
      toast.error("Could not start impersonation", { description: res.error });
      return;
    }
    toast.success(`Now viewing ${name}`, { description: "You are in tenant context as Super Admin." });
    navigate("/");
  };

  const handleSuspendToggle = async (t: PlatformTenant) => {
    const action = t.status === "SUSPENDED" ? "reactivate" : "suspend";
    try {
      if (action === "suspend") await platformTenantsApi.suspend(t.id);
      else await platformTenantsApi.reactivate(t.id);
      toast.success(`${t.name} ${action === "suspend" ? "suspended" : "reactivated"}`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Action failed", { description: errMsg(e) });
    }
  };

  const handleChangePlan = async (t: PlatformTenant, plan: BackendPlanTier) => {
    try {
      await platformTenantsApi.changePlan(t.id, plan);
      toast.success(`${t.name} plan updated to ${tierToLabel(plan)}`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Could not change plan", { description: errMsg(e) });
    }
  };

  const openEdit = (t: PlatformTenant) => {
    setEditing(t);
    setEditForm({
      name: t.name,
      industry: t.industry ?? "",
      region: t.region ?? "",
      status: t.status,
      primaryContact: t.primaryContact ?? "",
      contactEmail: t.contactEmail ?? "",
      renewalDate: t.renewalDate ? t.renewalDate.slice(0, 10) : "",
    });
  };

  const submitEdit = async () => {
    if (!editing || !editForm) return;
    if (!editForm.name.trim()) {
      toast.error("Tenant name is required");
      return;
    }
    setEditSubmitting(true);
    try {
      const body: UpdateTenantBody = {
        name: editForm.name.trim(),
        industry: editForm.industry.trim() || undefined,
        region: editForm.region.trim() || undefined,
        status: editForm.status,
        primaryContact: editForm.primaryContact.trim() || undefined,
        contactEmail: editForm.contactEmail.trim() || undefined,
        renewalDate: editForm.renewalDate ? new Date(editForm.renewalDate).toISOString() : undefined,
      };
      await platformTenantsApi.update(editing.id, body);
      toast.success(`${editing.name} updated`);
      setEditing(null);
      setEditForm(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Could not update tenant", { description: errMsg(e) });
    } finally {
      setEditSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteSubmitting(true);
    try {
      await platformTenantsApi.remove(deleting.id);
      toast.success(`${deleting.name} deleted`);
      setDeleting(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Could not delete tenant", { description: errMsg(e) });
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const submitNewTenant = async () => {
    const missing = validateRequired([
      { label: "Tenant name", value: form.name },
      { label: "Tenant code", value: form.code },
    ]);
    if (missing) {
      toast.error(missing);
      return;
    }
    setSubmitting(true);
    try {
      const body: CreateTenantBody = {
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        industry: form.industry.trim() || undefined,
        region: form.region.trim() || undefined,
        plan: form.plan,
        deploymentMode: "CLOUD",
        maxUsers: form.maxUsers ? Number(form.maxUsers) : undefined,
      };
      if (form.ownerName.trim() && form.ownerEmail.trim()) {
        body.owner = {
          name: form.ownerName.trim(),
          email: form.ownerEmail.trim(),
          phone: form.ownerPhone.trim() || undefined,
        };
      }
      const res = await platformTenantsApi.create(body);
      // Owner provisioning moved to a separate step (Clerk operations
      // can't transactionally span the tenant insert). Tell the admin
      // where to go next.
      toast.success(`Tenant ${res.name} created`, {
        description: form.ownerEmail.trim()
          ? `Now create the owner under Tenant → Users to send their invitation.`
          : "Use Tenant → Users to add the first admin.",
        duration: 8000,
      });
      setOpen(false);
      setForm(DEFAULT_FORM);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Could not create tenant", { description: errMsg(e) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <TenantSubNav />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {PLAN_OPTIONS.map(p => (
          <Card key={p.value} className="p-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{p.label}</div>
              <Badge variant="outline" className={planStyle[p.label]}>{planCounts[p.value]} tenants</Badge>
            </div>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="border-b pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              <CardTitle className="text-base">All Tenants ({tenants.length})</CardTitle>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tenants..." className="h-9 w-56 pl-8" />
              </div>
              <Select value={planFilter} onValueChange={setPlanFilter}>
                <SelectTrigger className="h-9 w-32"><SelectValue placeholder="Plan" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All plans</SelectItem>
                  {PLAN_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 w-32"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All status</SelectItem>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(DEFAULT_FORM); }}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-gradient-to-r from-accent to-primary hover:opacity-90">
                    <Plus className="mr-1.5 h-4 w-4" /> New Tenant
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Create new tenant</DialogTitle>
                    <DialogDescription>
                      Onboard a new customer organization. Owner contact info below is saved on
                      the tenant; create the actual admin user on the Tenant → Users page after
                      this — they'll receive an email invitation to set their password.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-3 py-2">
                    <div className="grid gap-1.5">
                      <Label>Tenant name</Label>
                      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Karya Logistics Sdn Bhd" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="grid gap-1.5">
                        <Label>Tenant code</Label>
                        <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="KARYA" />
                      </div>
                      <div className="grid gap-1.5">
                        <Label>Subscription plan</Label>
                        <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v as BackendPlanTier })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {PLAN_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="grid gap-1.5">
                        <Label>Industry</Label>
                        <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="Logistics" />
                      </div>
                      <div className="grid gap-1.5">
                        <Label>Region</Label>
                        <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Klang Valley" />
                      </div>
                    </div>
                    <div className="grid gap-1.5">
                      <Label>Max users</Label>
                      <Input type="number" value={form.maxUsers} onChange={(e) => setForm({ ...form, maxUsers: e.target.value })} placeholder="500" />
                    </div>
                    <div className="border-t pt-3">
                      <div className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">Primary contact (optional)</div>
                      <div className="mb-2 text-[11px] text-muted-foreground">
                        Saved as the tenant's primary contact. The actual admin user with sign-in
                        access is created on the Users page (separate Clerk invitation).
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-1.5"><Label>Contact name</Label><Input value={form.ownerName} onChange={(e) => setForm({ ...form, ownerName: e.target.value })} placeholder="Full name" /></div>
                        <div className="grid gap-1.5"><Label>Contact email</Label><Input type="email" value={form.ownerEmail} onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })} placeholder="admin@tenant.com" /></div>
                      </div>
                      <div className="mt-3 grid gap-1.5">
                        <Label>Contact phone</Label>
                        <Input value={form.ownerPhone} onChange={(e) => setForm({ ...form, ownerPhone: e.target.value })} placeholder="+60..." />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
                    <Button onClick={submitNewTenant} disabled={submitting}>
                      {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Create tenant
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead>Industry</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead className="text-right">Users</TableHead>
                <TableHead className="text-right">Sites</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Renewal</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading tenants...
                </TableCell></TableRow>
              )}
              {!loading && tenants.length === 0 && (
                <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">No tenants match your filters.</TableCell></TableRow>
              )}
              {!loading && tenants.map(t => {
                const planLabel = tierToLabel(t.plan.tier);
                const statusLabel = statusToLabel(t.status);
                return (
                  <TableRow key={t.id} className="cursor-pointer" onClick={() => navigate(`/platform/tenants/${t.id}`)}>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-primary/20 to-accent/20 text-[10px] font-semibold text-primary">{t.code.slice(0, 3)}</div>
                        <div>
                          <div className="text-sm font-medium">{t.name}</div>
                          <div className="text-[11px] text-muted-foreground">{t.code}{t.region ? ` · ${t.region}` : ""}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{t.industry ?? "—"}</TableCell>
                    <TableCell><Badge variant="outline" className={planStyle[planLabel]}>{planLabel}</Badge></TableCell>
                    <TableCell className="text-right text-sm font-medium">{t.counts.activeUsers.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-sm">{t.counts.sites}</TableCell>
                    <TableCell><Badge variant="outline" className={statusStyle[statusLabel]}>{statusLabel}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{t.renewalDate ? new Date(t.renewalDate).toLocaleDateString() : "—"}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem onClick={() => navigate(`/platform/tenants/${t.id}`)}><Eye className="mr-2 h-3.5 w-3.5" /> View tenant</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEdit(t)}><Edit3 className="mr-2 h-3.5 w-3.5" /> Edit tenant</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <div className="px-2 py-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Change plan</div>
                          {PLAN_OPTIONS.filter(p => p.value !== t.plan.tier).map(p => (
                            <DropdownMenuItem key={p.value} onClick={() => handleChangePlan(t, p.value)}>
                              <CreditCard className="mr-2 h-3.5 w-3.5" /> {p.label}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuItem onClick={() => toast.info("Feature flags coming soon")}><ToggleRight className="mr-2 h-3.5 w-3.5" /> Manage features</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleImpersonate(t.id, t.name)} className="text-accent focus:text-accent">
                            <ExternalLink className="mr-2 h-3.5 w-3.5" /> Open Tenant View
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleSuspendToggle(t)}>
                            <Power className="mr-2 h-3.5 w-3.5" /> {t.status === "SUSPENDED" ? "Reactivate" : "Suspend"}
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setDeleting(t)}>
                            <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete tenant
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setEditForm(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit tenant</DialogTitle>
            <DialogDescription>Update organization details and status.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="grid gap-3 py-2">
              <div className="grid gap-1.5">
                <Label>Tenant name</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label>Industry</Label>
                  <Input value={editForm.industry} onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })} />
                </div>
                <div className="grid gap-1.5">
                  <Label>Region</Label>
                  <Input value={editForm.region} onChange={(e) => setEditForm({ ...editForm, region: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label>Status</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v as BackendTenantStatus })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label>Renewal date</Label>
                  <Input type="date" value={editForm.renewalDate} onChange={(e) => setEditForm({ ...editForm, renewalDate: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label>Primary contact</Label>
                  <Input value={editForm.primaryContact} onChange={(e) => setEditForm({ ...editForm, primaryContact: e.target.value })} />
                </div>
                <div className="grid gap-1.5">
                  <Label>Contact email</Label>
                  <Input type="email" value={editForm.contactEmail} onChange={(e) => setEditForm({ ...editForm, contactEmail: e.target.value })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setEditForm(null); }} disabled={editSubmitting}>Cancel</Button>
            <Button onClick={submitEdit} disabled={editSubmitting}>
              {editSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the tenant and all of its data — users, sites, employees, attendance records, and requests. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleteSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Unexpected error";
}
